import { useCallback, useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/Button/Button'
import { TextInput } from '@/components/widget-components'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { Checkbox } from '@/components/Checkbox'
import { BirthdayInput } from '@/components/Select/BirthdayInput'
import { useCorpStore } from '@/reducers/corporateReducer'
import { ROUTES } from '../CorporateSignupPanel'
import axiosInstance from '@/requests/axios'
import { toPeople, updateCorporate } from '@/requests/corporate'
import { useEnrollmentHydration } from '@/hooks/useEnrollmentHydration'
import { TrashIcon } from '@radix-ui/react-icons'
import * as RadioGroup from '@radix-ui/react-radio-group'

interface SavedDirector {
  firstName: string
  lastName: string
  dateOfBirth: string
  occupation: string
  email: string
}

interface Owner {
  firstName: string
  lastName: string
  dateOfBirth: string
  occupation: string
  email: string
  isFromDirector?: boolean
  directorName?: string
}

interface ValidationError {
  directorEmail?: string
  ownershipStatus?: string
  owners?: {
    [key: number]: {
      firstName?: string
      lastName?: string
      dateOfBirth?: string
      occupation?: string
      email?: string
    }
  }
  submit?: string
}

const normalizeDateString = (dateString: string): string => {
  // Split the input into components
  const parts = dateString.split('-')

  // Pad month and day with leading zeros if needed
  return parts
    .map((part, index) => {
      if (index === 1 || index === 2) {
        // Month and day
        return part.padStart(2, '0')
      }
      return part // Year
    })
    .join('-')
}

export default function DirectorPersonalShare() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const nextState = useCorpStore((state) => state.nextState)
  const ownershipStatus = useCorpStore((state) => state.ownershipStatus)
  const setOwnershipStatus = useCorpStore((state) => state.setOwnershipStatus)
  const [owners, setOwners] = useState<Owner[]>([])

  const { data: currentUser } = useCurrentUser(true)

  const [errors, setErrors] = useState<ValidationError>({})
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({})

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [savedDirectors, setSavedDirectors] = useState<SavedDirector[]>([])
  const [selectedDirectors, setSelectedDirectors] = useState<{
    [key: string]: boolean
  }>({})

  // Reference to scrollable container
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  // Reference to the last added owner element
  const lastOwnerRef = useRef<HTMLDivElement>(null)

  const isValidDate = (dateString: string) => {
    dateString = normalizeDateString(dateString)

    if (!dateString) return false

    const dateComponents = dateString.split('-')
    if (
      dateComponents.length !== 3 ||
      dateComponents[0].length !== 4 || // Year
      dateComponents[1].length !== 2 || // Month
      dateComponents[2].length !== 2
    ) {
      // Day
      return false
    }

    const birthDate = new Date(dateString)
    const today = new Date()
    const minDate = new Date()
    minDate.setFullYear(today.getFullYear() - 120)

    // Calculate age precisely
    const age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    const dayDiff = today.getDate() - birthDate.getDate()
    const isNotYetBirthday = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)
    const finalAge = isNotYetBirthday ? age - 1 : age
    return (
      birthDate instanceof Date &&
      !isNaN(birthDate.getTime()) &&
      birthDate <= today &&
      birthDate >= minDate &&
      finalAge >= 18
    )
  }

  useEnrollmentHydration(
    useCallback(
      (enrollment) => {
        setSavedDirectors(
          enrollment.directors.map((director) => ({
            firstName: director.firstName,
            lastName: director.lastName,
            dateOfBirth: director.dateOfBirth,
            occupation: director.occupation,
            email: director.email,
          }))
        )

        if (enrollment.hasBeneficialOwners === null) return
        setOwnershipStatus(enrollment.hasBeneficialOwners)

        const directorNames = new Set(
          enrollment.directors.map((director) =>
            `${director.firstName} ${director.lastName}`.trim()
          )
        )
        const restored = enrollment.beneficialOwners.map((owner) => {
          const fullName = `${owner.firstName} ${owner.lastName}`.trim()
          const isFromDirector = directorNames.has(fullName)
          return {
            firstName: owner.firstName,
            lastName: owner.lastName,
            dateOfBirth: owner.dateOfBirth,
            occupation: owner.occupation,
            email: owner.email,
            ...(isFromDirector && { isFromDirector, directorName: fullName }),
          }
        })
        setOwners(restored)
        setSelectedDirectors(
          Object.fromEntries(
            restored
              .filter((owner) => owner.isFromDirector)
              .map((owner) => [owner.directorName as string, true])
          )
        )
      },
      [setOwnershipStatus]
    )
  )

  // Effect to scroll to the last added director owner
  useEffect(() => {
    if (lastOwnerRef.current && scrollContainerRef.current) {
      setTimeout(() => {
        lastOwnerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        })
      }, 100)
    }
  }, [owners.length])

  const handleOwnershipChange = (value: boolean) => {
    const newOwnershipStatus = value
    let newOwners = [...owners] // Copy current owners

    if (!value) {
      newOwners = []
      setOwners(newOwners) // Update state
    }

    setOwnershipStatus(newOwnershipStatus)
    setTouched((prev) => ({ ...prev, ownershipStatus: true }))

    // Validate with updated values immediately
    const formErrors = validateForm(newOwnershipStatus, newOwners)
    setErrors(formErrors)
  }

  const handleOwnerChange = (
    index: number,
    field: keyof Owner,
    value: string
  ) => {
    const newOwners = [...owners]

    const directorOwners = newOwners.filter((owner) => owner.isFromDirector)
    const actualIndex = directorOwners.length + index

    // Update the copy directly
    newOwners[actualIndex] = {
      ...newOwners[actualIndex],
      [field]: value,
    }

    // Update state with modified copy
    setOwners(newOwners)

    setTouched((prev) => ({ ...prev, [`${actualIndex}-${field}`]: true }))

    // Validate using the already-updated newOwners copy
    const formErrors = validateForm(ownershipStatus, newOwners)
    setErrors(formErrors)
  }

  const validateForm = (
    ownershipStatusToValidate: boolean | null | undefined,
    ownersToValidate: Owner[]
  ): ValidationError => {
    console.log(ownersToValidate, 'ownersToValidate')
    const newErrors: ValidationError = {}

    if (
      ownershipStatusToValidate === null ||
      ownershipStatusToValidate === undefined
    ) {
      newErrors.ownershipStatus = t(
        'directorPersonalShare.errors.ownershipRequired'
      )
    }

    if (ownershipStatusToValidate === true) {
      const ownerErrors: ValidationError['owners'] = {}

      ownersToValidate.forEach((owner, index) => {
        const currentErrors: any = {}

        if (!owner.firstName?.trim()) {
          currentErrors.firstName = t(
            'directorPersonalShare.errors.firstNameRequired'
          )
        }

        if (!owner.lastName?.trim()) {
          currentErrors.lastName = t(
            'directorPersonalShare.errors.lastNameRequired'
          )
        }

        if (!owner.email?.trim()) {
          currentErrors.email = t('directorPersonalShare.errors.emailRequired')
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(owner.email)) {
          currentErrors.email = t('directorPersonalShare.errors.emailInvalid')
        }

        if (!owner.occupation?.trim()) {
          currentErrors.occupation = t(
            'directorPersonalShare.errors.occupationRequired'
          )
        }

        if (!owner.dateOfBirth) {
          currentErrors.dateOfBirth = t(
            'directorPersonalShare.errors.dateOfBirthRequired'
          )
        } else if (!isValidDate(owner.dateOfBirth)) {
          const dateComponents = owner.dateOfBirth.split('-')
          if (
            dateComponents.length !== 3 ||
            dateComponents[0].length !== 4 ||
            dateComponents[1].length !== 2 ||
            dateComponents[2].length !== 2
          ) {
            currentErrors.dateOfBirth = t(
              'directorPersonalShare.errors.dateOfBirthIncomplete'
            )
          } else {
            currentErrors.dateOfBirth = t(
              'directorPersonalShare.errors.ageRestriction'
            )
          }
        }

        if (Object.keys(currentErrors).length > 0) {
          ownerErrors[index] = currentErrors
        }
      })

      if (Object.keys(ownerErrors).length > 0) {
        newErrors.owners = ownerErrors
      }
    }

    return newErrors
  }

  const addOwner = () => {
    const totalOwners = owners.length
    if (totalOwners < 4) {
      setOwners([
        ...owners,
        {
          firstName: '',
          lastName: '',
          dateOfBirth: '',
          occupation: '',
          email: '',
        },
      ])
    }
  }
  const removeOwner = (index: number) => {
    // Get only manual owners
    const manualOwners = owners.filter((owner) => !owner.isFromDirector)
    if (manualOwners.length > 0) {
      // Find the actual index in the full owners array
      const directorOwners = owners.filter((owner) => owner.isFromDirector)
      const actualIndex = directorOwners.length + index

      const newOwners = [...owners]
      newOwners.splice(actualIndex, 1)
      setOwners(newOwners)

      // Clear errors and touched state for removed owner
      const newErrors = { ...errors }
      if (newErrors.owners) {
        delete newErrors.owners[actualIndex]
        if (Object.keys(newErrors.owners).length === 0) {
          delete newErrors.owners
        }
      }
      setErrors(newErrors)

      const newTouched = { ...touched }
      Object.keys(newTouched).forEach((key) => {
        if (key.startsWith(`${actualIndex}-`)) {
          delete newTouched[key]
        }
      })
      setTouched(newTouched)
    }
  }

  const handleSubmit = async () => {
    // Mark all fields as touched first
    const allTouched: { [key: string]: boolean } = {
      ownershipStatus: true,
    }
    if (ownershipStatus) {
      owners.forEach((_, index) => {
        allTouched[`${index}-firstName`] = true
        allTouched[`${index}-lastName`] = true
        allTouched[`${index}-dateOfBirth`] = true
        allTouched[`${index}-occupation`] = true
        allTouched[`${index}-email`] = true
      })
    }
    setTouched(allTouched)

    // Validate all fields
    const formErrors = validateForm(ownershipStatus, owners)
    setErrors(formErrors)

    if (Object.keys(formErrors).length > 0 || isSubmitting) {
      return
    }

    setIsSubmitting(true)

    try {
      await updateCorporate({
        hasBeneficialOwners: ownershipStatus ?? false,
        beneficialOwners: ownershipStatus ? toPeople(owners) : [],
      })

      nextState()
      navigate(ROUTES.DIRECTOR_AUTH_INFO)
    } catch (error) {
      console.error('Error updating director information:', error)
      setErrors((prev) => ({
        ...prev,
        submit: t('directorPersonalShare.errors.submitError'),
      }))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDirectorSelect = (director: SavedDirector, checked: boolean) => {
    const directorFullName = `${director.firstName} ${director.lastName}`

    setSelectedDirectors((prev) => ({
      ...prev,
      [directorFullName]: checked,
    }))

    if (checked) {
      // Add director to owners list
      const newOwner: Owner = {
        firstName: director.firstName,
        lastName: director.lastName,
        dateOfBirth: director.dateOfBirth,
        occupation: director.occupation,
        isFromDirector: true,
        email: director.email,
        directorName: directorFullName,
      }
      setOwners((prev) => [...prev, newOwner])
    } else {
      // Remove director from owners list
      setOwners((prev) =>
        prev.filter((owner) => owner.directorName !== directorFullName)
      )
    }
  }

  const areOwnerFieldsFilled = (owner: Owner): boolean => {
    return (
      owner.firstName.trim() !== '' &&
      owner.lastName.trim() !== '' &&
      owner.dateOfBirth !== '' &&
      owner.occupation.trim() !== '' &&
      owner.email.trim() !== '' &&
      isValidDate(owner.dateOfBirth)
    )
  }

  const handleInputKeyDown = async (e: React.KeyboardEvent) => {
    const isFormComplete =
      !isSubmitting &&
      ownershipStatus !== null &&
      ownershipStatus !== undefined &&
      (!ownershipStatus ||
        (ownershipStatus === true &&
          owners.length > 0 &&
          owners.every((owner) => areOwnerFieldsFilled(owner))))

    if (e.key === 'Enter' && isFormComplete) {
      e.preventDefault()
      await handleSubmit()
    }
  }

  useEffect(() => {
    const handleDocumentKeyDown = async (e: KeyboardEvent) => {
      const isFormComplete =
        !isSubmitting &&
        ownershipStatus !== null &&
        ownershipStatus !== undefined &&
        (!ownershipStatus ||
          (ownershipStatus === true &&
            owners.length > 0 &&
            owners.every((owner) => areOwnerFieldsFilled(owner))))

      if (e.key === 'Enter' && isFormComplete) {
        e.preventDefault()
        await handleSubmit()
      }
    }

    document.addEventListener('keydown', handleDocumentKeyDown)
    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown)
    }
  }, [isSubmitting, ownershipStatus, owners])

  return (
    <div
      className="flex flex-col h-[600px]"
      tabIndex={-1}
      data-testid="container-director-share"
    >
      {/* Fixed Header Section */}
      <div className="flex-none space-y-6 mb-4">
        <div className="flex flex-col gap-1">
          <div
            className="text-lg font-normal uppercase"
            data-testid="text-director-share-title"
          >
            {t('directorPersonalShare.title')}
          </div>
          <p
            className="text-sm text-gray-500"
            data-testid="text-director-share-ubo-requirement"
          >
            {t('directorPersonalShare.uboRequirement')}
          </p>
        </div>

        <div
          className="flex flex-col gap-1 rounded border border-gray-300 p-3 dark:border-gray-600"
          data-testid="container-director-share-paytrie-account"
        >
          <span
            className="text-xs uppercase text-gray-500"
            data-testid="text-director-share-paytrie-account-label"
          >
            {t('directorPersonalShare.paytrieAccount.label')}
          </span>
          <span
            className="text-base break-all"
            data-testid="text-director-share-paytrie-account-email"
          >
            {currentUser?.email ?? ''}
          </span>
          <span
            className="text-xs text-gray-500"
            data-testid="text-director-share-paytrie-account-hint"
          >
            {t('directorPersonalShare.paytrieAccount.hint')}
          </span>
        </div>

        <div
          className="flex flex-col gap-2"
          data-testid="container-director-share-ownership"
        >
          <p
            className="text-base"
            data-testid="text-director-share-ownership-question"
          >
            {t('directorPersonalShare.ownershipQuestion')}
          </p>
          <RadioGroup.Root
            value={ownershipStatus === null ? '' : ownershipStatus.toString()}
            onValueChange={(value) => handleOwnershipChange(value === 'true')}
            className="flex items-center gap-8"
            data-testid="radio-group-director-share-ownership"
          >
            <div
              className="flex items-center space-x-3"
              data-testid="container-director-share-option-yes"
            >
              <RadioGroup.Item
                value="true"
                id="ownership-yes"
                className="w-4 h-4 rounded-full border border-gray-300 dark:border-gray-600"
                data-testid="radio-director-share-yes"
              >
                <RadioGroup.Indicator className="flex items-center justify-center w-full h-full relative after:content-[''] after:block after:w-2 after:h-2 after:rounded-full after:bg-black dark:after:bg-white" />
              </RadioGroup.Item>
              <label
                htmlFor="ownership-yes"
                className="text-md text-gray-900 dark:text-gray-100"
                data-testid="text-director-share-yes-label"
              >
                {t('directorPersonalShare.yes')}
              </label>
            </div>

            <div
              className="flex items-center space-x-3"
              data-testid="container-director-share-option-no"
            >
              <RadioGroup.Item
                value="false"
                id="ownership-no"
                className="w-4 h-4 rounded-full border border-gray-300 dark:border-gray-600"
                data-testid="radio-director-share-no"
              >
                <RadioGroup.Indicator className="flex items-center justify-center w-full h-full relative after:content-[''] after:block after:w-2 after:h-2 after:rounded-full after:bg-black dark:after:bg-white" />
              </RadioGroup.Item>
              <label
                htmlFor="ownership-no"
                className="text-md text-gray-900 dark:text-gray-100"
                data-testid="text-director-share-no-label"
              >
                {t('directorPersonalShare.no')}
              </label>
            </div>
          </RadioGroup.Root>
          {touched.ownershipStatus && errors.ownershipStatus && (
            <span
              className="text-sm text-red-500 mt-1"
              data-testid="error-director-share-ownership"
            >
              {errors.ownershipStatus}
            </span>
          )}
        </div>
      </div>

      {/* Scrollable Owners Section */}
      <div
        className="flex-1 min-h-0 overflow-y-auto"
        ref={scrollContainerRef}
        data-testid="container-director-share-owners"
      >
        {/* Add Directors Checkbox Section */}

        {ownershipStatus && (
          <div className="space-y-4 pr-2">
            {savedDirectors.length > 0 && (
              <div
                className="mb-6 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg"
                data-testid="container-director-share-saved"
              >
                <h3
                  className="font-semibold mb-4"
                  data-testid="text-director-share-saved-title"
                >
                  {t('directorPersonalShare.ownersSection.title')}
                </h3>
                <div className="space-y-3">
                  {savedDirectors.map((director, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2"
                      data-testid={`item-director-share-saved-${idx}`}
                    >
                      <Checkbox
                        checked={
                          selectedDirectors[
                            `${director.firstName} ${director.lastName}`
                          ] || false
                        }
                        onCheckedChange={(checked) =>
                          handleDirectorSelect(director, checked as boolean)
                        }
                        label={`${director.firstName} ${director.lastName}`}
                        data-testid={`check-director-share-saved-${idx}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {owners
              .filter((owner) => owner.isFromDirector)
              .map((owner, index) => (
                <div
                  key={`director-${index}`}
                  className="flex flex-col gap-6 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  ref={
                    index ===
                      owners.filter((o) => o.isFromDirector).length - 1 &&
                    owners.length ===
                      owners.filter((o) => o.isFromDirector).length
                      ? lastOwnerRef
                      : null
                  }
                  data-testid={`container-director-share-director-${index}`}
                >
                  <div className="flex justify-between items-center">
                    <h3
                      className="font-semibold"
                      data-testid={`text-director-share-director-title-${index}`}
                    >
                      {t('directorPersonalShare.ownerCard.title', {
                        number: index + 1,
                      })}
                    </h3>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                      <TextInput
                        placeholder={t(
                          'directorPersonalShare.firstName.placeholder'
                        )}
                        value={owner.firstName}
                        onChange={() => {}} // Empty handler since it's disabled
                        onKeyDown={handleInputKeyDown}
                        data-testid={`input-director-share-director-firstname-${index}`}
                      />
                    </div>

                    <div className="flex flex-col gap-1">
                      <TextInput
                        placeholder={t(
                          'directorPersonalShare.lastName.placeholder'
                        )}
                        value={owner.lastName}
                        onChange={() => {}} // Empty handler since it's disabled
                        onKeyDown={handleInputKeyDown}
                        data-testid={`input-director-share-director-lastname-${index}`}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <BirthdayInput
                      value={owner.dateOfBirth}
                      onChange={() => {}} // Empty handler since it's disabled
                      data-testid={`input-director-share-director-dob-${index}`}
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <TextInput
                      placeholder={t(
                        'directorPersonalShare.occupation.placeholder'
                      )}
                      value={owner.occupation}
                      disabled={true}
                      data-testid={`input-director-share-director-occupation-${index}`}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <TextInput
                      placeholder={t('directorPersonalShare.email.placeholder')}
                      value={owner.email}
                      type="email"
                      disabled={true}
                      data-testid={`input-director-share-director-email-${index}`}
                    />
                  </div>
                </div>
              ))}

            {/* Then show manually added owners */}
            {owners
              .filter((owner) => !owner.isFromDirector)
              .map((owner, index) => {
                const totalCheckedDirectors = owners.filter(
                  (o) => o.isFromDirector
                ).length
                const displayIndex = index + totalCheckedDirectors + 1
                const isLastOwner =
                  totalCheckedDirectors + index === owners.length - 1

                return (
                  <div
                    key={`manual-${index}`}
                    className="flex flex-col gap-6 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    ref={isLastOwner ? lastOwnerRef : null}
                    data-testid={`container-director-share-owner-${index}`}
                  >
                    <div className="flex justify-between items-center">
                      <h3
                        className="font-semibold"
                        data-testid={`text-director-share-owner-title-${index}`}
                      >
                        {t('directorPersonalShare.ownerCard.title', {
                          number: displayIndex,
                        })}
                      </h3>

                      <button
                        onClick={() => removeOwner(index)}
                        className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-50 transition-colors"
                        data-testid={`btn-director-share-owner-remove-${index}`}
                      >
                        <TrashIcon />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex flex-col gap-1">
                        <TextInput
                          placeholder={t(
                            'directorPersonalShare.firstName.placeholder'
                          )}
                          value={owner.firstName}
                          onChange={(e) =>
                            handleOwnerChange(
                              index,
                              'firstName',
                              e.target.value
                            )
                          }
                          data-testid={`input-director-share-owner-firstname-${index}`}
                        />
                        {touched[
                          `${index + totalCheckedDirectors}-firstName`
                        ] &&
                          errors.owners?.[index + totalCheckedDirectors]
                            ?.firstName && (
                            <span
                              className="text-sm text-red-500 mt-1"
                              data-testid={`error-director-share-owner-firstname-${index}`}
                            >
                              {
                                errors.owners[index + totalCheckedDirectors]
                                  .firstName
                              }
                            </span>
                          )}
                      </div>

                      <div className="flex flex-col gap-1">
                        <TextInput
                          placeholder={t(
                            'directorPersonalShare.lastName.placeholder'
                          )}
                          value={owner.lastName}
                          onChange={(e) =>
                            handleOwnerChange(index, 'lastName', e.target.value)
                          }
                          data-testid={`input-director-share-owner-lastname-${index}`}
                        />
                        {touched[`${index + totalCheckedDirectors}-lastName`] &&
                          errors.owners?.[index + totalCheckedDirectors]
                            ?.lastName && (
                            <span
                              className="text-sm text-red-500 mt-1"
                              data-testid={`error-director-share-owner-lastname-${index}`}
                            >
                              {
                                errors.owners[index + totalCheckedDirectors]
                                  .lastName
                              }
                            </span>
                          )}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <BirthdayInput
                        value={owner.dateOfBirth}
                        onChange={(value) =>
                          handleOwnerChange(index, 'dateOfBirth', value)
                        }
                        data-testid={`input-director-share-owner-dob-${index}`}
                      />
                      {touched[
                        `${index + totalCheckedDirectors}-dateOfBirth`
                      ] &&
                        errors.owners?.[index + totalCheckedDirectors]
                          ?.dateOfBirth && (
                          <span
                            className="text-sm text-red-500 mt-1"
                            data-testid={`error-director-share-owner-dob-${index}`}
                          >
                            {
                              errors.owners[index + totalCheckedDirectors]
                                .dateOfBirth
                            }
                          </span>
                        )}
                    </div>

                    <div className="flex flex-col gap-1">
                      <TextInput
                        placeholder={t(
                          'directorPersonalShare.occupation.placeholder'
                        )}
                        value={owner.occupation}
                        onChange={(e) =>
                          handleOwnerChange(index, 'occupation', e.target.value)
                        }
                        data-testid={`input-director-share-owner-occupation-${index}`}
                      />
                      {touched[`${index + totalCheckedDirectors}-occupation`] &&
                        errors.owners?.[index + totalCheckedDirectors]
                          ?.occupation && (
                          <span
                            className="text-sm text-red-500 mt-1"
                            data-testid={`error-director-share-owner-occupation-${index}`}
                          >
                            {
                              errors.owners[index + totalCheckedDirectors]
                                .occupation
                            }
                          </span>
                        )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <TextInput
                        placeholder={t(
                          'directorPersonalShare.email.placeholder'
                        )}
                        type="email"
                        value={owner.email}
                        onChange={(e) =>
                          handleOwnerChange(index, 'email', e.target.value)
                        }
                        data-testid={`input-director-share-owner-email-${index}`}
                      />
                      {touched[`${index + totalCheckedDirectors}-email`] &&
                        errors.owners?.[index + totalCheckedDirectors]
                          ?.email && (
                          <span
                            className="text-sm text-red-500 mt-1"
                            data-testid={`error-director-share-owner-email-${index}`}
                          >
                            {errors.owners[index + totalCheckedDirectors].email}
                          </span>
                        )}
                    </div>
                  </div>
                )
              })}

            <>
              {owners.filter((owner) => !owner.isFromDirector).length < 4 && (
                <Button
                  onClick={addOwner}
                  className="w-full flex items-center justify-center mb-4"
                  data-testid="btn-director-share-add-owner"
                >
                  {t('directorPersonalShare.addButton')}
                </Button>
              )}
            </>
          </div>
        )}
      </div>

      <div className="flex-none pt-4">
        {errors.submit && (
          <span
            className="text-sm text-red-500 block mb-4"
            data-testid="error-director-share-submit"
          >
            {errors.submit}
          </span>
        )}

        <Button
          onClick={handleSubmit}
          disabled={
            isSubmitting ||
            ownershipStatus === null ||
            ownershipStatus === undefined ||
            (ownershipStatus === true &&
              (owners.length === 0 ||
                !owners.every((owner) => areOwnerFieldsFilled(owner))))
          }
          className="inline-flex items-center py-8 px-4 w-full justify-center lg:gap-2"
          data-testid="btn-director-share-submit"
        >
          {isSubmitting
            ? t('directorPersonalShare.submitButton.saving')
            : t('directorPersonalShare.submitButton.continue')}
        </Button>
      </div>
    </div>
  )
}
